import { useRef, type ReactNode } from "react";
import { Modal } from "./Modal";

export interface ConfirmDialogProps {
  title: string;
  /** The body. Kept as children so the copy lives at the call site, where the
      thing being confirmed is known. */
  children: ReactNode;
  confirmLabel: string;
  /** Shown on the confirm button while the write is in flight. */
  busyLabel?: string;
  busy?: boolean;
  /** Held off until the body has a complete answer, e.g. a choice made. */
  confirmDisabled?: boolean;
  /** Paints the confirm as destructive, for the choice that takes something
      away. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The console's modal, for the actions that must not happen by accident.
 *
 * Creating an account is one: it reaches Privy, mints an identity and a
 * wallet, and the console has no delete. Changing a member's role is the
 * other: it is reversible, but it grants or removes console access, and it
 * used to be a button sitting in the page where a stray click landed. Smaller
 * things still confirm in place (the revoke row on Staff, the preview step in
 * `SendComposer`).
 *
 * Focus starts on CANCEL, not confirm. The trigger is a submit button, and an
 * Enter still held from the form would otherwise land on the destructive
 * choice. It returns to whatever opened the dialog on close, so a keyboard
 * user is put back where they were.
 *
 * On a phone it is a sheet from the bottom edge, its buttons full width and
 * within the thumb's reach, the safe choice still first.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busyLabel,
  busy = false,
  confirmDisabled = false,
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Escape and a pointer outside the panel close it too (see Modal). A dialog
  // that only closed by its own button would be the one thing on the screen
  // that traps you.
  return (
    <Modal labelledBy="dialog-title" initialFocus={cancelRef} onClose={onCancel}>
      {(close) => (
        <>
          <h2 id="dialog-title">{title}</h2>
          <div className="stack dialog-body">{children}</div>
          <div className="row dialog-actions">
            <button type="button" ref={cancelRef} disabled={busy} onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className={danger ? "danger" : "primary"}
              disabled={busy || confirmDisabled}
              onClick={onConfirm}
            >
              {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
